// Global "snap to top on route change" guard. Issue #176 surface #3
// (Android S8 / mobile post-login lands mid-document).
//
// Why a fresh component instead of reusing LayoutScrollRestoration:
//   • LayoutScrollRestoration lives under DashboardLayout only. Routes
//     outside that tree (/, /robot, /comprar, /login, /admin) inherit
//     whatever scrollY the outgoing route left behind. On Samsung S8 with
//     a slow layout pass, the per-emitter `scrollTo(0,0)` we added in
//     LoginFormContext.onSuccess (commit 37a6e3fe) fires before the new
//     route paints, so the operator sees the destination route already
//     scrolled into the middle of the document.
//   • ScrollRestorationToTop runs at the AppShell root, *every* route
//     change, and uses useLayoutEffect so the scroll reset commits in the
//     same browser paint as the destination route's first render — never
//     a frame later. It deliberately skips POP navigations so the
//     dashboard tree's back/forward scroll-restore stays intact.
//
// Contract (issue #176-2):
//   • After any push/replace navigate (e.g. login → /, login → /robot,
//     login → /comprar), window.scrollY === 0 before the next paint of
//     the new route.
//   • POP (browser back/forward) is left alone so the dashboard tree's
//     LayoutScrollRestoration can restore the saved offset.
import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export function ScrollRestorationToTop(): null {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  // useLayoutEffect (not useEffect): the scroll reset must land in the
  // same commit as the destination route's first paint. A normal effect
  // would let the browser draw the new route at the old scrollY for one
  // frame, exactly the "broken no meio" symptom reported on S8.
  useLayoutEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, navigationType]);

  return null;
}
